import React, { useState, useEffect, useRef } from 'react';
import { Organization, uploadOrganizationLogo, deleteOrganizationLogo } from '../../services/api/adminOrganizationService';
import {
  TaskyButton,
  TaskyCheckbox,
  TaskyDialog,
  TaskyFormGroup,
  TaskyInput,
  useConfirm,
} from '../tasky';
import { useToast } from '../../contexts/ToastContext';

interface OrganizationFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization?: Organization;
  onSubmit: (organizationData: Partial<Organization>) => void;
  onLogoUpdate?: () => void; // ロゴ更新後のコールバック
}

// 機能フラグのチェックボックス定義（表示順は従来のまま）
const FEATURE_FLAGS: { name: keyof Organization & string; label: string; help: string }[] = [
  { name: 'enable_ai_knowledge', label: 'AI資料（Knowledge）機能を有効にする', help: 'この組織でAI資料機能を使用可能にします' },
  { name: 'enable_product_catalog', label: '商品一覧（Product）機能を有効にする', help: 'この組織で商品一覧機能を使用可能にします' },
  { name: 'enable_user_created_agents', label: 'ユーザー作成エージェント機能を有効にする', help: 'この組織でユーザーが独自のAIエージェントを作成できるようにします' },
  { name: 'enable_super_agent', label: 'スーパーエージェント機能を有効にする', help: 'この組織でスーパーエージェント機能を使用可能にします' },
  { name: 'enable_api_integration', label: 'API連携（Google Analytics等）機能を有効にする', help: 'この組織でAPI連携機能を使用可能にします' },
  { name: 'enable_video_material', label: '動画生成（動画資料）機能を有効にする', help: 'この組織で動画資料（Remotion）機能を使用可能にします' },
  { name: 'enable_ad_score', label: '広告効果予測機能を有効にする', help: 'この組織で画像生成時の広告効果予測スコア（Vertex AI）を表示します' },
  { name: 'enable_lp_creation', label: 'LP制作機能を有効にする', help: 'この組織でLP（ランディングページ）制作機能を使用可能にします' },
  { name: 'enable_music_creation', label: '音楽生成機能を有効にする', help: 'この組織でAI音楽生成（Lyria）機能を使用可能にします' },
  { name: 'enable_carousel_creation', label: 'カルーセル制作機能を有効にする', help: 'この組織でカルーセル制作機能を使用可能にします' },
  { name: 'enable_movie_creation', label: 'ムービー制作機能を有効にする', help: 'この組織でムービー制作（β）機能を使用可能にします（デフォルト: 無効）' },
  // 歌広告・UGC動画広告は動画広告・漫画広告と同じくカード未登録ロックのみで制御するため、
  // 組織ごとのトグルは出さない（切り替えても表示は変わらないため）。
  { name: 'enable_easy_video', label: '動画かんたん作成', help: 'この組織で動画広告の「かんたん作成」タブ（3問に答えるだけで広告動画を自動作成）を使用可能にします（デフォルト: 無効）' },
  { name: 'enable_ai_doc3', label: 'AI資料3機能を有効にする', help: 'この組織でAI資料3（Document V3）機能を使用可能にします（デフォルト: 無効）' },
  { name: 'enable_chat_tools', label: 'チャットAIツール（商品登録・バナー生成）を有効にする', help: 'この組織でチャット（GPT-5）から商品登録・バナー生成などの内部ツールをAIが実行できるようにします（デフォルト: 無効）' },
  { name: 'enable_ad_operations', label: '広告運用機能を有効にする', help: 'この組織で広告運用ダッシュボードを使用可能にします' },
  { name: 'enable_news', label: 'ニュース機能を有効にする', help: 'この組織でニュースを表示します' },
  { name: 'enable_auto_banner_ab_test', label: 'オートバナーABテスト機能を有効にする', help: 'この組織で LLM ペルソナ集団によるバナーのABテスト（β）を使用可能にします（デフォルト: 無効）' },
];

const OrganizationFormModal: React.FC<OrganizationFormModalProps> = ({
  isOpen,
  onClose,
  organization,
  onSubmit,
  onLogoUpdate
}) => {
  const confirm = useConfirm();
  const { showToast } = useToast();

  // フォームの状態
  const [formData, setFormData] = useState<Partial<Organization>>({
    name: '',
    member_limit: 100,
    monthly_token_limit: 1000000,
    max_concurrent_image_jobs: 20,
    is_active: true,
    enable_ai_knowledge: true,
    enable_product_catalog: true,
    enable_user_created_agents: false,
    enable_super_agent: false,
    enable_api_integration: false,
    enable_video_material: false,
    enable_ad_score: false,
    enable_lp_creation: false,
    enable_music_creation: false,
    enable_carousel_creation: false,
    enable_movie_creation: false,
    enable_song_ad_creation: false,
    enable_easy_video: false,
    enable_ai_doc3: false,
    enable_chat_tools: false,
    enable_ad_operations: false,
    enable_news: false,
    enable_auto_banner_ab_test: false
  });

  // 検証エラー
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ロゴアップロード関連の状態
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 組織データが変更されたらフォームデータを更新
  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || '',
        // null / 負数は無制限（Organization::hasReachedMemberLimit）。-1 で表示して無制限のまま保存できるようにする。
        member_limit: organization.member_limit == null || organization.member_limit < 0 ? -1 : organization.member_limit,
        monthly_token_limit: organization.monthly_token_limit || 1000000,
        max_concurrent_image_jobs: Math.max(10, organization.max_concurrent_image_jobs ?? 10),
        is_active: organization.is_active !== undefined ? organization.is_active : true,
        enable_ai_knowledge: organization.enable_ai_knowledge !== undefined ? organization.enable_ai_knowledge : true,
        enable_product_catalog: organization.enable_product_catalog !== undefined ? organization.enable_product_catalog : true,
        enable_user_created_agents: organization.enable_user_created_agents !== undefined ? organization.enable_user_created_agents : false,
        enable_super_agent: organization.enable_super_agent !== undefined ? organization.enable_super_agent : false,
        enable_api_integration: organization.enable_api_integration !== undefined ? organization.enable_api_integration : false,
        enable_video_material: organization.enable_video_material !== undefined ? organization.enable_video_material : false,
        enable_ad_score: organization.enable_ad_score !== undefined ? organization.enable_ad_score : false,
        enable_lp_creation: organization.enable_lp_creation !== undefined ? organization.enable_lp_creation : false,
        enable_music_creation: organization.enable_music_creation !== undefined ? organization.enable_music_creation : false,
        enable_carousel_creation: organization.enable_carousel_creation !== undefined ? organization.enable_carousel_creation : false,
        enable_movie_creation: organization.enable_movie_creation !== undefined ? organization.enable_movie_creation : false,
        enable_song_ad_creation: organization.enable_song_ad_creation !== undefined ? organization.enable_song_ad_creation : false,
        enable_easy_video: organization.enable_easy_video !== undefined ? organization.enable_easy_video : false,
        enable_ai_doc3: organization.enable_ai_doc3 !== undefined ? organization.enable_ai_doc3 : false,
        enable_chat_tools: organization.enable_chat_tools !== undefined ? organization.enable_chat_tools : false,
        enable_ad_operations: organization.enable_ad_operations !== undefined ? organization.enable_ad_operations : false,
        enable_news: organization.enable_news !== undefined ? organization.enable_news : false,
        enable_auto_banner_ab_test: organization.enable_auto_banner_ab_test !== undefined ? organization.enable_auto_banner_ab_test : false
      });
    } else {
      // 新規作成モードの場合はデフォルト値にリセット
      setFormData({
        name: '',
        member_limit: 100,
        monthly_token_limit: 1000000,
        max_concurrent_image_jobs: 20,
        is_active: true,
        enable_ai_knowledge: true,
        enable_product_catalog: true,
        enable_user_created_agents: false,
        enable_super_agent: false,
        enable_api_integration: false,
        enable_video_material: false,
        enable_ad_score: false,
        enable_lp_creation: false,
        enable_music_creation: false,
        enable_carousel_creation: false,
        enable_movie_creation: false,
        enable_song_ad_creation: false,
        enable_easy_video: false,
        enable_ai_doc3: false,
        enable_chat_tools: false,
        enable_ad_operations: false,
        enable_news: false,
        enable_auto_banner_ab_test: false
      });
    }
  }, [organization]);

  // 入力変更ハンドラー
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox'
        ? (e.target as HTMLInputElement).checked
        : (type === 'number' ? parseInt(value, 10) : value)
    }));
  };

  // ロゴファイル選択ハンドラー
  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // ファイルサイズチェック（2MB以下）
      if (file.size > 2 * 1024 * 1024) {
        showToast('ファイルサイズは2MB以下にしてください', 'error');
        return;
      }

      // ファイル形式チェック
      if (!file.type.startsWith('image/')) {
        showToast('画像ファイルを選択してください', 'error');
        return;
      }

      setLogoFile(file);

      // プレビュー作成
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // ロゴアップロードハンドラー
  const handleLogoUpload = async () => {
    if (!logoFile || !organization?.id) return;

    try {
      setIsLogoUploading(true);
      await uploadOrganizationLogo(organization.id, logoFile);
      showToast('ロゴが正常にアップロードされました', 'success');

      // プレビューとファイルをリセット
      setLogoFile(null);
      setLogoPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // 親コンポーネントにロゴ更新を通知
      if (onLogoUpdate) {
        onLogoUpdate();
      }
    } catch (error) {
      console.error('ロゴアップロードエラー:', error);
      showToast('ロゴのアップロードに失敗しました', 'error');
    } finally {
      setIsLogoUploading(false);
    }
  };

  // ロゴ削除ハンドラー
  const handleLogoDelete = async () => {
    if (!organization?.id || !organization.logo_path) return;

    if (!(await confirm({ title: 'ロゴを削除してもよろしいですか？', tone: 'destructive', confirmText: '削除する' }))) return;

    try {
      setIsLogoUploading(true);
      await deleteOrganizationLogo(organization.id);
      showToast('ロゴが正常に削除されました', 'success');

      // 親コンポーネントにロゴ更新を通知
      if (onLogoUpdate) {
        onLogoUpdate();
      }
    } catch (error) {
      console.error('ロゴ削除エラー:', error);
      showToast('ロゴの削除に失敗しました', 'error');
    } finally {
      setIsLogoUploading(false);
    }
  };

  // ファイル選択ダイアログを開く
  const handleLogoSelectClick = () => {
    fileInputRef.current?.click();
  };

  // フォーム送信
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 必須項目のバリデーション
    const newErrors: Record<string, string> = {};
    if (!formData.name?.trim()) {
      newErrors.name = '組織名は必須です';
    }

    // メンバー上限: 1 以上の整数、または -1（無制限）。0 は誰も追加できない上限になるので不可。
    // 空欄は parseInt で NaN になるため、整数判定で弾く（以前は 0 / NaN が falsy で素通りしていた）。
    const memberLimit = formData.member_limit;
    if (memberLimit === undefined || !Number.isInteger(memberLimit) || (memberLimit !== -1 && memberLimit < 1)) {
      newErrors.member_limit = 'メンバー上限は1以上（無制限は-1）の整数を設定してください';
    }

    const monthlyLimit = formData.monthly_token_limit;
    if (monthlyLimit === undefined || Number.isNaN(monthlyLimit) || monthlyLimit < 0) {
      newErrors.monthly_token_limit = '月間クレジット上限は1以上の値を設定してください';
    }

    // 同時画像生成ジョブ上限は任意項目。空欄（NaN）は「指定なし」として送信データから外す
    // （NaN は JSON で null になり backend の integer 検証で 422 になるため）。
    const concurrentJobs = formData.max_concurrent_image_jobs;
    const hasConcurrentJobs = concurrentJobs !== undefined && concurrentJobs !== null && !Number.isNaN(concurrentJobs);
    if (hasConcurrentJobs && (concurrentJobs < 10 || concurrentJobs > 20)) {
      newErrors.max_concurrent_image_jobs = '同時画像生成ジョブ上限は10〜20の値を設定してください';
    }

    setErrors(newErrors);

    // エラーがなければ送信
    if (Object.keys(newErrors).length === 0) {
      if (hasConcurrentJobs) {
        onSubmit(formData);
      } else {
        const withoutConcurrentJobs = { ...formData };
        delete withoutConcurrentJobs.max_concurrent_image_jobs;
        onSubmit(withoutConcurrentJobs);
      }
    }
  };

  return (
    <TaskyDialog
      open={isOpen}
      onClose={onClose}
      title={organization ? '組織の編集' : '新規組織の作成'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <TaskyFormGroup label="組織名" required htmlFor="name" error={errors.name}>
          <TaskyInput
            type="text"
            id="name"
            name="name"
            value={formData.name || ''}
            onChange={handleChange}
            hasError={Boolean(errors.name)}
          />
        </TaskyFormGroup>

        <TaskyFormGroup
          label="メンバー上限"
          required
          htmlFor="member_limit"
          error={errors.member_limit}
          helper="組織に所属できるメンバーの最大数（-1 で無制限）"
        >
          <TaskyInput
            type="number"
            id="member_limit"
            name="member_limit"
            value={formData.member_limit || ''}
            onChange={handleChange}
            min="-1"
            hasError={Boolean(errors.member_limit)}
          />
        </TaskyFormGroup>

        <TaskyFormGroup
          label="月間クレジット上限"
          required
          htmlFor="monthly_token_limit"
          error={errors.monthly_token_limit}
          helper="組織が毎月使用できるクレジットの上限"
        >
          <TaskyInput
            type="number"
            id="monthly_token_limit"
            name="monthly_token_limit"
            value={formData.monthly_token_limit || ''}
            onChange={handleChange}
            min="1"
            hasError={Boolean(errors.monthly_token_limit)}
          />
        </TaskyFormGroup>

        <TaskyFormGroup
          label="同時画像生成ジョブ上限"
          htmlFor="max_concurrent_image_jobs"
          error={errors.max_concurrent_image_jobs}
          helper="組織が同時に実行できる画像生成ジョブの上限（10〜20）"
        >
          <TaskyInput
            type="number"
            id="max_concurrent_image_jobs"
            name="max_concurrent_image_jobs"
            value={formData.max_concurrent_image_jobs || ''}
            onChange={handleChange}
            min="10"
            max="20"
            hasError={Boolean(errors.max_concurrent_image_jobs)}
          />
        </TaskyFormGroup>

        {/* ロゴアップロードセクション（既存組織の編集時のみ表示） */}
        {organization && (
          <TaskyFormGroup
            label="組織ロゴ"
            helper="JPG、PNG、GIF、SVG形式の画像ファイル（2MB以下）をアップロードできます"
          >
            <div className="rounded-tasky-sm border border-warm-border bg-warm-bg p-4 space-y-3">
              {/* 現在のロゴ表示 */}
              {organization.logo_path && (
                <div>
                  <img
                    src={`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/storage/${organization.logo_path}`}
                    alt="現在のロゴ"
                    className="max-w-[200px] max-h-[100px] object-contain bg-white rounded-tasky-xs p-1"
                  />
                  <div className="mt-2">
                    <TaskyButton
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleLogoDelete}
                      disabled={isLogoUploading}
                    >
                      ロゴを削除
                    </TaskyButton>
                  </div>
                </div>
              )}

              {/* ファイル選択 */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoFileChange}
                accept="image/*"
                className="hidden"
              />

              {/* プレビュー表示 */}
              {logoPreview && (
                <div>
                  <img
                    src={logoPreview}
                    alt="プレビュー"
                    className="max-w-[200px] max-h-[100px] object-contain bg-white rounded-tasky-xs p-1"
                  />
                </div>
              )}

              {/* アップロードボタン */}
              <div className="flex gap-2">
                <TaskyButton
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleLogoSelectClick}
                  disabled={isLogoUploading}
                >
                  ファイルを選択
                </TaskyButton>

                {logoFile && (
                  <TaskyButton
                    type="button"
                    size="sm"
                    onClick={handleLogoUpload}
                    disabled={isLogoUploading}
                  >
                    {isLogoUploading ? 'アップロード中...' : 'アップロード'}
                  </TaskyButton>
                )}
              </div>
            </div>
          </TaskyFormGroup>
        )}

        <div className="border-t border-warm-border pt-4">
          <TaskyCheckbox
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            label="組織を有効にする"
          />
          <p className="text-xs text-gray-500 mt-1 ml-6">
            無効にすると、この組織のメンバーはログインできなくなります
          </p>
        </div>

        <div className="border-t border-warm-border pt-4 space-y-3">
          <h3 className="text-sm font-semibold text-dark">機能設定</h3>
          {FEATURE_FLAGS.map(flag => (
            <div key={flag.name}>
              <TaskyCheckbox
                name={flag.name}
                checked={Boolean(formData[flag.name])}
                onChange={handleChange}
                label={flag.label}
              />
              <p className="text-xs text-gray-500 mt-1 ml-6">{flag.help}</p>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 border-t border-warm-border mt-4 pt-4">
          <TaskyButton type="button" variant="outline" size="sm" onClick={onClose}>
            キャンセル
          </TaskyButton>
          <TaskyButton type="submit" size="sm">
            {organization ? '更新' : '作成'}
          </TaskyButton>
        </div>
      </form>
    </TaskyDialog>
  );
};

export default OrganizationFormModal;
